// La scheda di una connessione in Strumenti, dentro il gruppo del suo tipo (tela «PopTown Pop
// Style», artboard Strumenti): nome, descrizione, indirizzo, chi la usa, stato e gli strumenti,
// ognuno con il suo interruttore. Gmail si ricollega (autorizzazione scaduta o revocata),
// Pushover cambia i destinatari; le altre connessioni rileggono gli strumenti dal server.
import { useState } from 'react';
import type { ToolGroup } from '../../lib/api';
import type { Tint } from '../../components/KindGroup';
import { Button } from '../../components/Button';
import { Icon } from '../../components/Icon';
import { PopSwitch } from '../../components/KindGroup';
import { TextArea } from '../../components/Field';
import { ParamsLine } from './ParamsField';

export const ICON: Record<string, string> = {
  workspace: 'box',
  web: 'globe',
  memo: 'brain',
  mcp: 'plug-zap',
  gmail: 'mail',
  pushover: 'bell',
  api: 'code',
  cli: 'terminal',
  pianifica: 'calendar-clock',
};

/** La tinta di ogni tipo, come sulla tela */
export const TINT: Record<string, Tint> = {
  workspace: 'blue',
  web: 'lilac',
  api: 'sun',
  memo: 'mint',
  gmail: 'coral',
  pushover: 'pink',
  mcp: 'blue',
  cli: 'lilac',
  pianifica: 'sun',
};

// la descrizione dello strumento API porta in coda il servizio, che la scheda dice già sopra
const short = (text: string) => text.split(' Servizio «')[0];

export function ToolCard({
  group: g,
  onRemove,
  onRefresh,
  onToggle,
  onDescribe,
  onReconnect,
  onRecipients,
  onParams,
}: {
  group: ToolGroup;
  onRemove: () => void;
  onRefresh: () => void;
  onToggle: (name: string, on: boolean) => void;
  onDescribe: (text: string) => void;
  /** Gmail: di nuovo il consenso di Google */
  onReconnect: () => void;
  /** Pushover: cambia i destinatari */
  onRecipients: () => void;
  /** API: cambia lo schema dei parametri */
  onParams: (text: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(g.description);
  const granted = g.tools.filter((t) => t.enabled).length;
  const where = g.account || g.url;
  return (
    <article className="conn-card">
      <div className="conn-head">
        <div style={{ flex: '1 1 260px', minWidth: 0 }}>
          <div className="conn-name">{g.name}</div>
          {editing ? (
            <div style={{ display: 'grid', gap: 8, marginTop: 6 }}>
              <TextArea value={draft} onChange={setDraft} rows={3} autoFocus />
              <div style={{ display: 'flex', gap: 8 }}>
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => {
                    onDescribe(draft.trim());
                    setEditing(false);
                  }}
                >
                  Salva
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
                  Annulla
                </Button>
              </div>
            </div>
          ) : (
            <div className="conn-desc">
              {g.description || 'Nessuna descrizione'}
              {g.removable && (
                <button
                  className="conn-link"
                  onClick={() => {
                    setDraft(g.description);
                    setEditing(true);
                  }}
                >
                  Modifica
                </button>
              )}
            </div>
          )}
          {where && <div className="conn-url">{where}</div>}
          {g.kind === 'api' && g.removable && !editing && (
            <ParamsLine value={g.params ?? ''} onSave={onParams} />
          )}
          {g.recipients && g.recipients.length > 0 && (
            <div className="conn-users">
              Destinatari:{' '}
              {g.recipients
                .map((r) => {
                  const devices = r.devices?.length
                    ? `, ${r.devices.length} ${r.devices.length === 1 ? 'dispositivo' : 'dispositivi'}`
                    : '';
                  return `${r.name} (${r.key}${devices})`;
                })
                .join(', ')}
            </div>
          )}
          {g.profiles && g.profiles.length > 0 && (
            <div className="conn-users">Lo usano: {g.profiles.join(', ')}</div>
          )}
          {g.error && (
            <div className="conn-users" style={{ color: 'var(--danger)' }}>
              {g.error}
            </div>
          )}
        </div>
        <span className="conn-badge">{g.removable ? 'Collegato' : 'Sempre attivo'}</span>
        {g.removable && (
          <>
            {g.kind === 'gmail' && (
              <Button variant="secondary" size="lg" icon="refresh-cw" onClick={onReconnect}>
                Ricollega
              </Button>
            )}
            {g.kind === 'pushover' && (
              <Button variant="secondary" size="lg" onClick={onRecipients}>
                Destinatari
              </Button>
            )}
            {g.kind !== 'gmail' && g.kind !== 'pushover' && (
              <Button variant="secondary" size="lg" onClick={onRefresh}>
                Aggiorna
              </Button>
            )}
            <button className="conn-unlink" onClick={onRemove}>
              Scollega
            </button>
          </>
        )}
      </div>
      <button className="conn-toggle" aria-expanded={open} onClick={() => setOpen(!open)}>
        <span style={{ display: 'grid', transform: open ? 'rotate(90deg)' : 'none' }}>
          <Icon name="chevron-right" size={16} />
        </span>
        {open
          ? 'Nascondi gli strumenti'
          : `${g.tools.length} ${g.tools.length === 1 ? 'strumento' : 'strumenti'} · ${granted} ${granted === 1 ? 'concesso' : 'concessi'} ai Pops`}
      </button>
      {open && (
        <ul className="conn-tools">
          {g.tools.map((t) => (
            <li key={t.name} className="conn-tool">
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="conn-tool-name">{t.name}</div>
                <div className="conn-tool-desc">{short(t.description)}</div>
                {t.profiles && t.profiles.length > 0 && (
                  <div className="conn-users">Lo usano: {t.profiles.join(', ')}</div>
                )}
              </div>
              <span className="conn-tool-state" data-on={t.enabled || !g.removable || undefined}>
                {t.enabled || !g.removable ? 'Concesso' : 'Non concesso'}
              </span>
              {g.removable && (
                <PopSwitch
                  label={t.name}
                  checked={t.enabled}
                  onChange={(v) => onToggle(t.name, v)}
                />
              )}
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}
